import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { Navigate, NavLink, useLocation, useOutlet } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { LayoutDashboard, ClipboardList, BookOpen, BarChart3, Settings, Clock, LogOut, Monitor, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { useStaffApi } from "@/lib/useStaffApi";
import { useAuth } from "@/lib/auth";
import { playNewOrderChime, unlockAudio } from "@/lib/chime";
import { autoPrintOn, printReceipt } from "@/lib/printReceipt";

const PanelCtx = createContext(null);
export const usePanel = () => useContext(PanelCtx);

const NAV = [
  { to: "/panel", label: "Dashboard", icon: LayoutDashboard, end: true, id: "side-dashboard" },
  { to: "/panel/jualan", label: "Jualan Gabungan", icon: TrendingUp, id: "side-jualan", admin: true },
  { to: "/panel/pesanan", label: "Pesanan", icon: ClipboardList, id: "side-pesanan", badge: true },
  { to: "/panel/menu", label: "Menu", icon: BookOpen, id: "side-menu" },
  { to: "/panel/laporan", label: "Laporan", icon: BarChart3, id: "side-laporan", admin: true },
  { to: "/panel/tetapan", label: "Tetapan", icon: Settings, id: "side-tetapan" },
  { to: "/pos", label: "Sistem POS", icon: Monitor, id: "side-pos" },
];

export const isAdmin = (me) => ["superadmin", "admin"].includes(me?.role);
export const isSale = (me) => me?.role === "sale";

export function AdminOnly({ children, allowSale }) {
  const { me } = usePanel();
  if (isSale(me)) return allowSale ? children : <Navigate to="/panel/jualan" replace />;
  return isAdmin(me) ? children : <Navigate to="/panel" replace />;
}

function Sidebar({ newCount, me }) {
  const nav = isSale(me) ? NAV.filter((n) => n.id === "side-jualan") : NAV.filter((n) => !n.admin || isAdmin(me));
  return (
    <aside className="bg-[#7F1D1D] text-white md:h-[100dvh] md:overflow-y-auto no-scrollbar md:sticky md:top-0 flex md:flex-col">
      <div className="hidden md:flex [@media(max-height:500px)]:hidden flex-col items-center py-6 border-b border-white/10">
        <div className="w-20 h-20 rounded-2xl bg-white p-1.5"><img src="/img/logo.png" alt="POKYA" className="w-full h-full object-contain" /></div>
        <p className="font-script text-amber-300 mt-2">Restaurant</p>
      </div>
      <nav className="flex md:flex-col gap-1 p-2 md:p-3 overflow-x-auto no-scrollbar w-full">
        {nav.map(({ to, label, icon: Icon, end, id, badge }) => (
          <NavLink key={to} to={to} end={end} data-testid={id}
            className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium shrink-0 transition-colors ${isActive ? "bg-[#DC2626] text-white" : "text-white/85 hover:bg-white/10"}`}>
            <Icon className="w-4 h-4" />{label}
            {badge && newCount > 0 && <span data-testid="side-new-badge" className="ml-auto min-w-[20px] h-5 px-1 rounded-full bg-red-500 text-[11px] font-bold flex items-center justify-center">{newCount}</span>}
          </NavLink>
        ))}
      </nav>
    </aside>
  );
}

function Pending({ me }) {
  const { logout: signOut } = useAuth();
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7] p-6">
      <div className="bg-white rounded-2xl border p-8 max-w-md text-center" data-testid="pending-approval">
        <Clock className="w-12 h-12 text-amber-500 mx-auto" />
        <h1 className="font-display text-xl font-bold mt-4">Menunggu Kelulusan Admin</h1>
        <p className="text-sm text-slate-600 mt-2">Akaun {me.email} belum diluluskan. Minta admin luluskan anda di Tetapan → Pengguna.</p>
        <button onClick={() => signOut()} data-testid="pending-signout" className="mt-6 px-5 h-10 rounded-full border text-sm font-semibold">Log Keluar</button>
      </div>
    </div>
  );
}

function Panel() {
  const req = useStaffApi();
  const [me, setMe] = useState(null);
  const [newCount, setNewCount] = useState(0);
  const [tick, setTick] = useState(0);
  const lastNew = useRef(null);
  const { pathname } = useLocation();
  const outlet = useOutlet();

  useEffect(() => { req("get", "/me").then(setMe).catch(() => setMe(false)); }, [req]);
  useEffect(() => {
    window.addEventListener("pointerdown", unlockAudio, { once: true });
    return () => window.removeEventListener("pointerdown", unlockAudio);
  }, []);

  const poll = useCallback(async () => {
    const d = await req("get", "/staff/orders?status=baharu").catch(() => null);
    if (!d) return;
    const latest = d.orders[0]?.created_at || "";
    if (lastNew.current !== null && latest > lastNew.current) {
      toast.success("Pesanan baharu masuk!");
      window.__lastChime = Date.now();
      playNewOrderChime();
      if (autoPrintOn()) d.orders.filter((o) => o.created_at > lastNew.current).reverse().forEach(printReceipt);
      setTick((t) => t + 1);
    }
    if (lastNew.current === null || latest > lastNew.current) lastNew.current = latest;
    setNewCount(d.counts.baharu);
  }, [req]);

  useEffect(() => {
    if (!me || me.role === "pending" || isSale(me)) return;
    poll();
    const t = setInterval(poll, 10000);
    return () => clearInterval(t);
  }, [me, poll]);

  useEffect(() => {
    if (!me || me.role === "pending" || isSale(me)) return;
    const sync = () => req("post", "/staff/loyverse/auto-sync").catch(() => {});
    sync();
    const t = setInterval(sync, 5 * 60000);
    return () => clearInterval(t);
  }, [me, req]);

  if (me === null) return <div className="p-10 text-sm text-slate-500">Memuatkan...</div>;
  if (me === false) return <div className="p-10 text-sm text-red-600">Gagal memuatkan akaun. Sila muat semula.</div>;
  if (me.role === "pending") return <Pending me={me} />;
  if (isSale(me) && pathname !== "/panel/jualan") return <Navigate to="/panel/jualan" replace />;

  return (
    <PanelCtx.Provider value={{ me, req, tick, refreshCount: poll }}>
      <div className="min-h-screen grid grid-cols-1 md:grid-cols-[230px_1fr] bg-[#F7F5EF]">
        <Sidebar newCount={newCount} me={me} />
        <main className="min-w-0">
          <header className="flex items-center justify-between px-6 md:px-10 h-16 border-b bg-white/80 backdrop-blur-xl sticky top-0 z-30">
            <h1 className="font-display font-bold text-lg truncate min-w-0">Panel Pekerja <span className="hidden lg:inline text-slate-400 font-medium">– POKYA RESTAURANT</span></h1>
            <div className="flex items-center gap-3">
              <span className="hidden sm:inline text-xs px-2 py-1 rounded-full bg-red-100 text-red-800 font-semibold capitalize" data-testid="panel-role">{me.role}</span>
              <UserMenu me={me} />
            </div>
          </header>
          <AnimatePresence mode="wait">
            <motion.div key={pathname} className="p-6 md:p-10" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}>
              {outlet}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
    </PanelCtx.Provider>
  );
}

function UserMenu({ me }) {
  const { logout } = useAuth();
  return (
    <div className="flex items-center gap-2">
      <span className="hidden md:inline text-xs text-slate-500" data-testid="panel-user-email">{me.email}</span>
      <button onClick={logout} data-testid="panel-logout-button" title="Log Keluar"
        className="w-9 h-9 rounded-full bg-[#B91C1C] text-white flex items-center justify-center hover:bg-[#7F1D1D] active:scale-90 transition-[background-color,transform]">
        <LogOut className="w-4 h-4" />
      </button>
    </div>
  );
}

export default function PanelLayout() {
  const { token } = useAuth();
  return token ? <Panel /> : <Navigate to="/sign-in" replace />;
}
